import { useMutation, useQueryClient } from "@tanstack/react-query"
import { type FormEvent, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"

import type { Movimiento } from "./tipos"

/**
 * Corregir lo que DESCRIBE a un movimiento: su concepto y su referencia.
 *
 * ⚠ **El importe, el sentido y la cuenta no se editan, y no es un olvido.** El ledger
 * de banco es append-only en lo que es dinero: cambiarlos reescribiría el saldo de un
 * día pasado sin dejar rastro de lo que decía antes, que es justo el fallo que este
 * ledger viene a impedir. Para eso está anular y volver a registrar, que deja las dos
 * filas a la vista y el extracto explicando el saldo.
 *
 * ⚠ **Sólo los movimientos tecleados dentro de la cuenta.** Un abono de una cuenta por
 * pagar, el pago de un rubro, la ejecución de una orden o media transferencia los
 * explica otro documento; corregirlos aquí haría que el mismo hecho se contara
 * distinto en dos sitios. El servidor lo vuelve a comprobar.
 */
export function DialogoEditarMovimiento({
  movimiento,
  onCerrar,
}: {
  /** El que se edita. `null` cierra el diálogo. */
  movimiento: Movimiento | null
  onCerrar: () => void
}) {
  const clienteQuery = useQueryClient()
  const [concepto, setConcepto] = useState("")
  const [referencia, setReferencia] = useState("")
  // La fila cambia sin desmontar el diálogo: se remonta el estado al abrir otra.
  const [ultimo, setUltimo] = useState<number | null>(null)
  if (movimiento !== null && movimiento.id !== ultimo) {
    setUltimo(movimiento.id)
    setConcepto(movimiento.concepto)
    setReferencia(movimiento.referencia ?? "")
  }

  const guardar = useMutation({
    mutationFn: () =>
      api.patch(`/api/v1/transacciones/${movimiento?.id}`, {
        concepto: concepto.trim(),
        referencia: referencia.trim() || null,
      }),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["movimientos"] })
      setUltimo(null)
      onCerrar()
    },
  })

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (concepto.trim().length >= 3) guardar.mutate()
  }

  return (
    <Modal
      abierto={movimiento !== null}
      titulo="Editar el movimiento"
      descripcion="El importe no se edita: para corregirlo, anúlalo y vuelve a registrarlo."
      ancho="sm"
      onCerrar={onCerrar}
    >
      <form onSubmit={enviar} className="grid gap-4">
        <label className="block space-y-1 text-sm" htmlFor="editar-concepto">
          <span className="font-medium">Concepto</span>
          <input
            id="editar-concepto"
            value={concepto}
            onChange={(e) => setConcepto(e.target.value)}
            maxLength={200}
            className="campo w-full"
          />
        </label>
        <label className="block space-y-1 text-sm" htmlFor="editar-referencia">
          <span className="font-medium">Referencia</span>
          <input
            id="editar-referencia"
            value={referencia}
            onChange={(e) => setReferencia(e.target.value)}
            maxLength={60}
            className="campo w-full tabular-nums"
          />
        </label>
        {guardar.isError && (
          <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
            {guardar.error instanceof ErrorApi ? guardar.error.message : "No se pudo guardar"}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Boton variante="fantasma" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton
            variante="primario"
            type="submit"
            disabled={guardar.isPending || concepto.trim().length < 3}
          >
            {guardar.isPending ? "Guardando…" : "Guardar"}
          </Boton>
        </div>
      </form>
    </Modal>
  )
}
